<!-- /Users/xingyunx/workplace/workspace/RingcentralAnalysisPortal/src/components/snapshot/SnapshotPreviewDialog.vue -->
<!-- Apple-inspired snapshot preview dialog with horizontal button layout -->
<!-- Full-bleed image display with Cancel (left) and Share (right) buttons at bottom -->
<!-- RELEVANT FILES: src/composables/useSnapshot.ts, src/components/analytics/StaffPerformance.vue, src/components/analytics/DashboardAnalytics.vue -->
<script setup lang="ts">
import { computed, watch } from 'vue';
import { Share2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface Props {
  /** Whether the dialog is open */
  open: boolean;
  /** Generated image file and filename */
  generatedImage: { file: File; filename: string } | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  /** Emitted when dialog open state changes */
  'update:open': [value: boolean];
  /** Emitted when user clicks Share button */
  share: [];
}>();

// Create preview URL from the generated image file
const previewImageUrl = computed(() => {
  if (!props.generatedImage) {return null;}
  return URL.createObjectURL(props.generatedImage.file);
});

// Clean up object URL when dialog closes to prevent memory leaks
watch(
  () => props.open,
  (isOpen, wasOpen) => {
    if (wasOpen && !isOpen && previewImageUrl.value) {
      URL.revokeObjectURL(previewImageUrl.value);
    }
  },
);

// Handle dialog close
function handleClose() {
  emit('update:open', false);
}

// Handle share button click
function handleShare() {
  emit('share');
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="max-w-3xl p-0 gap-0 overflow-hidden">
      <!-- Image Preview - Full bleed design -->
      <div v-if="previewImageUrl" class="w-full overflow-auto max-h-[70vh] bg-white">
        <img :src="previewImageUrl" alt="Snapshot preview" class="w-full" />
      </div>

      <!-- Actions Bar - Full-width buttons with 8px gap -->
      <div class="grid grid-cols-2 gap-2 p-4 border-t bg-white">
        <!-- Cancel Button (Secondary) -->
        <Button variant="outline" class="h-12" @click="handleClose"> Cancel </Button>

        <!-- Share Button (Primary) -->
        <Button class="h-12" @click="handleShare">
          <Share2 class="h-4 w-4" />
          Share
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
